<template>
  <div class="jqe" v-if="question">
    <!-- type picker -->
    <div class="jqe-types">
      <div v-for="group in groups" :key="group" class="jqe-types__group"
           :class="{'is-active': group === activeGroup}">
        <span class="jqe-types__title">{{ msg('questiongroup_' + group) }}</span>
        <div class="jqe-types__grid">
          <button v-for="type in typesOf(group)" :key="type.value" type="button"
                  class="jqe-type" :class="{'is-active': question.type === type.value}"
                  v-b-tooltip.hover :title="msg(type.label)"
                  @click="patch({type: type.value})">
            <feather-icon :icon="type.icon" size="15"/>
          </button>
        </div>
      </div>
    </div>

    <b-row>
      <b-col cols="12" md="8">
        <b-form-group :label="msg('questionlabel')">
          <b-form-input :value="question.label" :placeholder="msg('questionlabel')"
                        @input="patch({label: $event})"/>
        </b-form-group>
      </b-col>
      <b-col cols="12" md="4">
        <b-form-group :label="msg('questionkey')">
          <b-form-input :value="question.key" :state="keyState"
                        @input="patch({key: slug($event)})"/>
          <small class="text-muted">{{ msg('questionkeyhint') }}</small>
        </b-form-group>
      </b-col>

      <b-col cols="12" md="8">
        <b-form-group :label="msg('questionhelp')">
          <b-form-input :value="question.helpText" @input="patch({helpText: $event})"/>
        </b-form-group>
      </b-col>
      <b-col cols="6" md="2">
        <b-form-group>
          <template #label>{{ msg('questionrequired') }}<info-hint :text="msg('info_required')"/></template>
          <b-form-checkbox switch :checked="question.required === true"
                           :disabled="typeSupports('noAnswer')"
                           @change="patch({required: $event})"/>
        </b-form-group>
      </b-col>
      <b-col cols="6" md="2">
        <b-form-group>
          <template #label>{{ msg('questionwidth') }}<info-hint :text="msg('info_size')"/></template>
          <v-select append-to-body :value="question.size || 12" :options="[3,4,6,8,12]"
                    :clearable="false" :dir="isRTL ? 'rtl' : 'ltr'"
                    @input="patch({size: $event})"/>
        </b-form-group>
      </b-col>

      <b-col v-if="typeSupports('placeholder')" cols="12" md="6">
        <b-form-group :label="msg('questionplaceholder')">
          <b-form-input :value="question.placeholder" @input="patch({placeholder: $event})"/>
        </b-form-group>
      </b-col>
      <!-- booking task steps -->
      <b-col v-if="typeSupports('payment')" cols="12" md="6">
        <b-form-group :label="msg('paymenttype')">
          <v-select append-to-body :value="question.paymentType" :options="paymentTypes"
                    label="text" :reduce="o => o.value" :clearable="false"
                    :dir="isRTL ? 'rtl' : 'ltr'" :placeholder="msg('paymenttype')"
                    @input="patch({paymentType: $event})"/>
        </b-form-group>
      </b-col>
      <b-col v-if="typeSupports('action')" cols="6" md="3">
        <b-form-group>
          <template #label>{{ msg('blockingstep') }}<info-hint :text="msg('info_blocking')"/></template>
          <!-- guest details is always blocking: the check-in depends on it -->
          <b-form-checkbox switch :checked="question.type === 'GUEST_DETAILS' || question.blocking === true"
                           :disabled="question.type === 'GUEST_DETAILS'"
                           @change="patch({blocking: $event})"/>
        </b-form-group>
      </b-col>
      <b-col v-if="typeSupports('action')" cols="12">
        <b-alert show variant="secondary" class="jqe-taskhint">
          <feather-icon icon="InfoIcon" size="14" class="mr-50"/>
          {{ msg('taskstephint') }}
        </b-alert>
      </b-col>

      <b-col v-if="typeSupports('prefill')" cols="12" md="6">
        <b-form-group>
          <template #label>{{ msg('prefillfrom') }}<info-hint :text="msg('info_prefill')"/></template>
          <v-select append-to-body :value="question.prefillFrom || 'NONE'" :options="prefillOptions"
                    label="text" :reduce="o => o.value" :clearable="false" :dir="isRTL ? 'rtl' : 'ltr'"
                    @input="patch({prefillFrom: $event})"/>
        </b-form-group>
      </b-col>

      <!-- number bounds -->
      <b-col v-if="typeSupports('minMax')" cols="6" md="3">
        <b-form-group :label="msg('min')">
          <b-form-input type="number" :value="question.minNumber"
                        @input="patch({minNumber: $event === '' ? null : Number($event)})"/>
        </b-form-group>
      </b-col>
      <b-col v-if="typeSupports('minMax')" cols="6" md="3">
        <b-form-group :label="msg('max')">
          <b-form-input type="number" :value="question.maxNumber"
                        @input="patch({maxNumber: $event === '' ? null : Number($event)})"/>
        </b-form-group>
      </b-col>

      <!-- date bounds -->
      <b-col v-if="typeSupports('dateRange')" cols="6" md="3">
        <b-form-group :label="msg('mindate')">
          <b-form-input type="date" :value="question.minDate" @input="patch({minDate: $event})"/>
        </b-form-group>
      </b-col>
      <b-col v-if="typeSupports('dateRange')" cols="6" md="3">
        <b-form-group :label="msg('maxdate')">
          <b-form-input type="date" :value="question.maxDate" @input="patch({maxDate: $event})"/>
        </b-form-group>
      </b-col>

      <!-- rating -->
      <b-col v-if="typeSupports('rating')" cols="6" md="3">
        <b-form-group :label="msg('maxrating')">
          <b-form-input type="number" min="3" max="10" :value="question.maxRating || 5"
                        @input="patch({maxRating: Number($event) || 5})"/>
        </b-form-group>
      </b-col>

      <!-- info content -->
      <b-col v-if="typeSupports('content')" cols="12">
        <b-form-group :label="msg('content')">
          <b-form-textarea rows="3" :value="question.content" @input="patch({content: $event})"/>
        </b-form-group>
      </b-col>

      <!-- file upload -->
      <b-col v-if="typeSupports('file')" cols="12" md="4">
        <b-form-group :label="msg('fileaccept')">
          <b-form-input :value="question.fileAccept" placeholder="image/*,application/pdf"
                        @input="patch({fileAccept: $event})"/>
        </b-form-group>
      </b-col>
      <b-col v-if="typeSupports('file')" cols="6" md="4">
        <b-form-group :label="msg('filemaxmb')">
          <b-form-input type="number" min="1" :value="question.fileMaxMb || 10"
                        @input="patch({fileMaxMb: Number($event) || 10})"/>
        </b-form-group>
      </b-col>
      <b-col v-if="typeSupports('file')" cols="6" md="4">
        <b-form-group :label="msg('filemaxcount')">
          <b-form-input type="number" min="1" :value="question.fileMaxCount || 3"
                        @input="patch({fileMaxCount: Number($event) || 3})"/>
        </b-form-group>
      </b-col>

      <!-- product picker -->
      <b-col v-if="typeSupports('productList')" cols="12">
        <b-form-group>
          <template #label>{{ msg('offeredproducts') }}<info-hint :text="msg('info_offeredproducts')"/></template>
          <v-select append-to-body multiple :value="question.guestFeatureIds || []" :options="features"
                    label="name" :reduce="feature => feature.id" :dir="isRTL ? 'rtl' : 'ltr'"
                    :placeholder="msg('allproducts')"
                    @input="patch({guestFeatureIds: $event})"/>
        </b-form-group>
      </b-col>
      <b-col v-if="typeSupports('multiple')" cols="6" md="4">
        <b-form-group>
          <template #label>{{ msg('allowmultiple') }}<info-hint :text="msg('info_multipleproducts')"/></template>
          <b-form-checkbox switch :checked="question.multipleProducts === true"
                           @change="patch({multipleProducts: $event})"/>
        </b-form-group>
      </b-col>
      <b-col v-if="typeSupports('productList')" cols="6" md="4">
        <b-form-group>
          <template #label>{{ msg('allowamount') }}<info-hint :text="msg('info_allowamount')"/></template>
          <b-form-checkbox switch :checked="question.amountAllowed === true"
                           @change="patch({amountAllowed: $event})"/>
        </b-form-group>
      </b-col>
      <b-col v-if="typeSupports('productList')" cols="6" md="4">
        <!-- with a BEFORE {checkInTime} condition this IS the automatic early-check-in upsell -->
        <b-form-group>
          <template #label>{{ msg('autoselect') }}<info-hint :text="msg('info_autoselect')"/></template>
          <b-form-checkbox switch :checked="question.autoSelect === true"
                           @change="patch({autoSelect: $event})"/>
        </b-form-group>
      </b-col>
      <b-col v-if="typeSupports('productList')" cols="12">
        <!-- the time-based availability lives in the condition editor below;
             without this pointer nobody finds it -->
        <b-alert show variant="info" class="jqe-taskhint">
          <feather-icon icon="ClockIcon" size="14" class="mr-50"/>
          {{ msg('productconditionhint') }}
        </b-alert>
      </b-col>
    </b-row>

    <!-- options -->
    <template v-if="typeSupports('options')">
      <div class="jqe-section">
        <span class="jqe-section__title">{{ msg('options') }}</span>
        <b-form-checkbox switch :checked="question.allowOther === true"
                         @change="patch({allowOther: $event})">
          {{ msg('allowother') }}<info-hint :text="msg('info_allowother')"/>
        </b-form-checkbox>
      </div>
      <journey-option-editor :value="question.options || []" :features="features"
                             @input="patch({options: $event})"/>
    </template>

    <!-- condition -->
    <journey-condition-editor
        class="mt-1"
        :value="question.condition"
        :available-questions="precedingQuestions"
        :all-questions="allQuestions"
        @input="patch({condition: $event})"
    />
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import JourneyOptionEditor from './journeyOptionEditor.vue'
import JourneyConditionEditor from './journeyConditionEditor.vue'
import InfoHint from './InfoHint.vue'
import {PAYMENT_TYPES, PREFILL_OPTIONS, QUESTION_GROUPS, supports, typesByGroup} from '@/model/journey/journeyTypes'

/** Right pane of the builder: edits the selected question. */
export default {
  name: 'journeyQuestionEditor',
  components: {JourneyOptionEditor, JourneyConditionEditor, InfoHint},
  props: {
    value: {type: Object, default: null},
    features: {type: Array, default: () => []},
    /** questions before this one (for conditions) */
    precedingQuestions: {type: Array, default: () => []},
    allQuestions: {type: Array, default: () => []},
    duplicateKey: {type: Boolean, default: false},
  },
  computed: {
    ...mapGetters('translator', ['msg']),
    ...mapGetters('appConfig', ['isRTL']),
    question() {
      return this.value;
    },
    groups() {
      return QUESTION_GROUPS;
    },
    /** The group the current question's type belongs to, so the header of that
     *  category can be highlighted — one small icon turning purple among ~20 is
     *  easy to miss. */
    activeGroup() {
      if (this.question == null || this.question.type == null) return null;
      return QUESTION_GROUPS.find(group =>
          typesByGroup(group).some(type => type.value === this.question.type)) || null;
    },
    prefillOptions() {
      return PREFILL_OPTIONS.map(option => ({...option, text: this.msg(option.label)}));
    },
    paymentTypes() {
      return PAYMENT_TYPES.map(option => ({...option, text: this.msg(option.label)}));
    },
    keyState() {
      if (!this.question || !this.question.key) return false;
      return this.duplicateKey ? false : null;
    },
  },
  methods: {
    typesOf(group) {
      return typesByGroup(group);
    },
    typeSupports(feature) {
      return this.question ? supports(this.question.type, feature) : false;
    },
    patch(patch) {
      this.$emit('input', {...this.question, ...patch});
    },
    slug(value) {
      return (value || '').toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '');
    },
  }
}
</script>

<style lang="scss" scoped>
.jqe-types {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(115, 103, 240, .05);
  margin-bottom: 14px;

  &__group {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  &__title {
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: #6e6b7b;
    font-weight: 700;
    transition: color .15s ease;
  }

  &__group.is-active {
    .jqe-types__title {
      color: #7367f0;
      font-weight: 800;
    }

    .jqe-types__grid {
      box-shadow: 0 0 0 2px rgba(115, 103, 240, .25);
      border-radius: 11px;
    }
  }

  &__grid {
    display: flex;
    gap: 4px;
  }
}

.jqe-type {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  border: 1px solid rgba(34, 41, 47, .12);
  background: #fff;
  color: #6e6b7b;
  cursor: pointer;

  &:hover {
    border-color: #7367f0;
    color: #7367f0;
  }

  &.is-active {
    background: #7367f0;
    border-color: #7367f0;
    color: #fff;
  }
}

.jqe-taskhint {
  padding: 8px 12px;
  margin-bottom: 0;
  font-size: 12.5px;
}

.jqe-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 6px 0;

  &__title {
    font-weight: 700;
    color: #5e5873;
  }
}

.dark-layout {
  /* the accent reads brighter on the dark panel */
  .jqe-types__group.is-active .jqe-types__title {
    color: #9e95f5;
  }

  .jqe-type {
    background: #283046;
    border-color: rgba(255, 255, 255, .1);
    color: #b4b7bd;
  }

  .jqe-section__title {
    color: #d0d2d6;
  }
}
</style>
